import React, { Suspense, lazy } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "./lib/auth";
import { useAuth } from "./lib/auth";
import RequireAuth from "./components/RequireAuth";
import AmbientMusic from "./components/AmbientMusic";
import LiveNotifications from "./components/LiveNotifications";
import { usePresence } from "./hooks/usePresence";

import BellaChatbot from "./components/BellaChatbot";

const LoginPage = lazy(() => import("./pages/LoginPage"));
const BellaOnboardingPage = lazy(() => import("./pages/BellaOnboardingPage"));
const OurStoryPage = lazy(() => import("./pages/OurStoryPage"));
const HomePage = lazy(() => import("./pages/HomePage"));
const AmorPage = lazy(() => import("./pages/AmorPage"));
const GalleryPage = lazy(() => import("./pages/GalleryPage"));
const SelfiePage = lazy(() => import("./pages/SelfiePage"));
const UploadPage = lazy(() => import("./pages/UploadPage"));
const GuestBookPage = lazy(() => import("./pages/GuestBookPage"));
const AdminPage = lazy(() => import("./pages/AdminPage"));
const VideosPage = lazy(() => import("./pages/VideosPage"));
const GuestPhotosPage = lazy(() => import("./pages/GuestPhotosPage"));
const DJRequestPage = lazy(() => import("./pages/DJRequestPage"));
const VenuePage = lazy(() => import("./pages/VenuePage"));
const OurJourneyPage = lazy(() => import("./pages/OurJourneyPage"));
const NotFoundPage = lazy(() => import("./pages/NotFoundPage"));

const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-[#FDFBF7]">
    <div className="animate-pulse text-[#A89878] tracking-widest uppercase text-sm" style={{ fontFamily: "'Playfair Display', serif" }}>
      Loading...
    </div>
  </div>
);

// Tracks current user's presence — must be inside BrowserRouter and AuthProvider
function PresenceTracker() {
  const { name, role } = useAuth();
  const location = useLocation();
  usePresence({ name, role, page: location.pathname });
  return null;
}

export default function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <PresenceTracker />
          <AmbientMusic />
          <LiveNotifications />

          <BellaChatbot />
          <Toaster position="top-center" richColors />
          <Suspense fallback={<PageLoader />}>
            <Routes>
              <Route path="/login" element={<LoginPage />} />
              <Route path="/bella-onboarding" element={<RequireAuth><BellaOnboardingPage /></RequireAuth>} />
              {/* Couple welcome page — first thing guests see */}
              <Route path="/our-story" element={<RequireAuth><OurStoryPage /></RequireAuth>} />
              <Route path="/" element={<RequireAuth><HomePage /></RequireAuth>} />
              <Route path="/gallery" element={<RequireAuth><GalleryPage /></RequireAuth>} />
              <Route path="/amor" element={<RequireAuth><AmorPage /></RequireAuth>} />
              <Route path="/selfie" element={<RequireAuth><SelfiePage /></RequireAuth>} />
              <Route path="/upload" element={<RequireAuth><UploadPage /></RequireAuth>} />
              <Route path="/guestbook" element={<RequireAuth><GuestBookPage /></RequireAuth>} />
              <Route path="/videos" element={<RequireAuth><VideosPage /></RequireAuth>} />
              <Route path="/guest-photos" element={<RequireAuth><GuestPhotosPage /></RequireAuth>} />
              <Route path="/dj-requests" element={<RequireAuth><DJRequestPage /></RequireAuth>} />
              <Route path="/venue" element={<RequireAuth><VenuePage /></RequireAuth>} />
              <Route path="/admin" element={<RequireAuth adminOnly><AdminPage /></RequireAuth>} />
              <Route path="/our-journey" element={<RequireAuth><OurJourneyPage /></RequireAuth>} />
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}
